import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  Injector,
  input,
  Signal,
  ViewChild,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router, RouterModule } from "@angular/router";
import { TranslocoModule } from "@ngneat/transloco";
import { MatTreeModule, MatTree } from "@angular/material/tree";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { filter, map } from "rxjs";
import { TreeNode } from "@core/services/tree-builder.service";
import { UserStateService } from "@core/services/common/user-state.service";
import { obtenerRamaActiva } from "./rama-activa.utils";

@Component({
  selector: "tree-menu",
  templateUrl: "tree-menu.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ["tree-menu.component.scss"],
  standalone: true,
  imports: [
    MatIconModule,
    TranslocoModule,
    MatButtonModule,
    RouterModule,
    MatTreeModule,
  ],
})
export class TreeMenuComponent {
  private readonly router = inject(Router);
  private readonly userState = inject(UserStateService);
  private readonly injector = inject(Injector);

  @ViewChild("tree") public tree!: MatTree<TreeNode>;

  public readonly dataMenuTree = input.required<TreeNode[]>();
  public readonly childrenAccessor = (node: TreeNode): TreeNode[] => node.children ?? [];
  public readonly hasChild = (_: number, node: TreeNode): boolean => node.tipo === "M";
  public readonly routing = this.userState.routing;

  private readonly urlActual: Signal<string> = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.urlAfterRedirects || event.url),
    ),
    { initialValue: this.router.url },
  );

  private readonly ramaActiva = computed<TreeNode[]>(() =>
    obtenerRamaActiva(this.dataMenuTree()[0]?.children ?? [], this.urlActual()),
  );

  /** Después del render, como el click: un expand durante el pintado del árbol no renderiza los hijos.
   *  Solo abre, así que no cierra lo que el usuario dejó abierto en otra rama. */
  private readonly expandirRamaActiva = effect(() => {
    const rama = this.ramaActiva();
    if (!rama.length) return;
    afterNextRender(() => rama.forEach((nodo) => this.tree?.expand(nodo)), {
      injector: this.injector,
    });
  });

  @HostListener("contextmenu", ["$event"])
  public onRightClick(event: MouseEvent): void {
    event.preventDefault();
  }

  public handleLinkInteraction(event: MouseEvent, path: string): void {
    const abrirEnNuevaPestana = event.button === 1 || event.ctrlKey || event.metaKey;
    if (abrirEnNuevaPestana) {
      event.preventDefault();
      this.openInNewTab(path);
    }
  }

  public isNodeExpanded(node: TreeNode): boolean {
    return this.tree?.isExpanded(node) || false;
  }

  private openInNewTab(path: string): void {
    const urlTree = this.router.createUrlTree([path]);
    const serializedUrl = this.router.serializeUrl(urlTree);
    window.open(serializedUrl, "_blank");
  }
}
